/**
 * mockDataService.ts — PRODUCTION SHIM (V33)
 * Real data served by apiService.ts.
 * mockData retained for Agent12 component demo mode — shapes match component interfaces exactly.
 */
export const isDemoMode = (): boolean => localStorage.getItem('demo_mode') === 'true';

export const setDemoMode = (enabled: boolean): void => {
  localStorage.setItem('demo_mode', enabled ? 'true' : 'false');
};

export const mockData = {
  // ActivityItem interface: activity_id, type, description, policy_id?, timestamp
  activities: () => [
    { activity_id: 'act1', type: 'approval' as const, description: 'Whale policy approved — ABC Logistics Commercial Auto ($22,140)', policy_id: 'p1', timestamp: new Date(Date.now() - 15 * 60000).toISOString() },
    { activity_id: 'act2', type: 'policy_bind' as const, description: 'Policy bound — Rivera Family Whole Life', policy_id: 'p2', timestamp: new Date(Date.now() - 45 * 60000).toISOString() },
    { activity_id: 'act3', type: 'approval' as const, description: 'Compliance override approved — GA OCI regulation update', policy_id: 'p3', timestamp: new Date(Date.now() - 90 * 60000).toISOString() },
    { activity_id: 'act4', type: 'rejection' as const, description: 'Policy rejected — missing carrier approval', policy_id: 'p4', timestamp: new Date(Date.now() - 120 * 60000).toISOString() },
  ],

  // ActivityStats interface: policies_bound_today, policies_bound_week, approvals_today,
  // rejections_today, average_decision_time, automation_rate
  activityStats: () => ({
    policies_bound_today: 7,
    policies_bound_week: 34,
    approvals_today: 12,
    rejections_today: 2,
    average_decision_time: 38,
    automation_rate: 95.8,
  }),

  // PendingApproval interface: approval_id, item_type, type, policy_id?, client_name?,
  // description, agent_11_notes?, priority, created_at
  // item_type must stay in sync with PendingApprovals.tsx's ItemType union
  // ('policy' | 'intervention' | 'escalation') — added when that component's
  // real (non-demo) data path was fixed to call the real approve/reject
  // endpoints, which are keyed by item_type. This demo array had fallen out
  // of sync with that change; item_type is restored here to match.
  approvals: () => [
    { approval_id: 'a1', item_type: 'policy' as const, type: 'whale_binding' as const, policy_id: 'p1', client_name: 'ABC Logistics Inc.', description: 'Commercial Auto Bundle — $22,140 premium. Whale threshold exceeded. All compliance checks passed.', agent_11_notes: 'GA state vetted. Carrier approved. Bias check passed.', priority: 'high' as const, created_at: new Date(Date.now() - 20 * 60000).toISOString() },
    { approval_id: 'a2', item_type: 'intervention' as const, type: 'compliance_override' as const, policy_id: 'p2', client_name: 'Rivera Family', description: 'Whole Life Insurance — new FL disclosure requirement detected. Agent 11 flagged for review.', agent_11_notes: 'FL Bulletin 2026-04 requires updated disclosure language.', priority: 'medium' as const, created_at: new Date(Date.now() - 35 * 60000).toISOString() },
  ],

  // WhalePolicy interface: policy_id, client_name, client_location, total_premium,
  // commission_value, policy_type, agent_9_notes, agent_11_status, compliance_checks, created_at
  whales: () => [
    { policy_id: 'w1', client_name: 'ABC Logistics Inc.', client_location: 'Atlanta, GA', total_premium: 22140, commission_value: 3321, policy_type: 'Commercial Auto Bundle', agent_9_notes: 'Client needs policy bound ASAP — current coverage expires EOD. Prefers highest liability limits. Strong relationship from previous personal lines.', agent_11_status: 'approved' as const, compliance_checks: { state_vetted: true, carrier_approved: true, bias_check_passed: true }, created_at: new Date(Date.now() - 45 * 60000).toISOString() },
    { policy_id: 'w2', client_name: 'Southeast Catering Group', client_location: 'Atlanta, GA', total_premium: 18750, commission_value: 2812, policy_type: 'Commercial General Liability', agent_9_notes: 'Multi-location food service operator. Clean claims history 5 years. Decision maker on-site today.', agent_11_status: 'approved' as const, compliance_checks: { state_vetted: true, carrier_approved: true, bias_check_passed: true }, created_at: new Date(Date.now() - 90 * 60000).toISOString() },
  ],

  // Agent12Dashboard dashboardStats — matches DashboardStats interface exactly
  dashboardStats: () => ({
    pending_approvals: 8,
    whale_opportunities: 3,
    policies_bound_today: 7,
    total_commission_today: 3850,
    agent_11_interventions: 5,
    agent_9_escalations: 3,
  }),
};

export * from './apiService';
